import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { checkInvitation, MIN_PASSWORD_LENGTH } from '../api/auth';
import { ApiError, OfflineError } from '../api/http';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import { useMeta } from './MetaContext';
import { localReturnPath, nextQuery } from './returnPath';
import { Wordmark } from './Wordmark';

/**
 * What the page knows about the code an invitation link brought: nothing asked
 * (no link, or the first account, which needs none), asking, good, not good, or
 * no answer (the platform would not say just now; the registration still checks).
 */
type LinkedInvite = 'none' | 'checking' | 'valid' | 'invalid' | 'unchecked';

/**
 * Registration by invitation. The code comes first, because without one there
 * is nothing to fill in: a link that carries it (`?invite=<code>`) is checked
 * as the page opens, so a good one shows as such in place of the field and a
 * spent or mistyped one says so before the person has typed anything. Checking
 * spends nothing; only creating the account counts a use.
 */
export function Register() {
  const { meta } = useMeta();
  const { me, loaded, signUp } = useAuth();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  // The invitation link carries the code, so whoever follows it types nothing: `?invite=<code>`.
  const fromLink = search.get('invite') ?? '';
  // Where the invitation was sent them: a session, usually. Followed once the account exists.
  const next = localReturnPath(search.get('next'));
  const [handle, setHandle] = useState('');
  // The name others see follows the username until the person writes one of their own.
  const [ownName, setOwnName] = useState<string | null>(null);
  const displayName = ownName ?? handle;
  const [password, setPassword] = useState('');
  const [inviteCode, setInviteCode] = useState(fromLink);
  // What the platform answered about the link's code; once the person types a code of their own
  // the answer is about something else and the page stops showing it.
  const [answer, setAnswer] = useState<{ code: string; state: LinkedInvite } | null>(null);
  const [typed, setTyped] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const needsInvite = !me.registrationOpen;
  const asked = loaded && needsInvite && fromLink !== '';
  const linked: LinkedInvite =
    !asked || typed ? 'none' : answer?.code === fromLink ? answer.state : 'checking';

  useEffect(() => {
    if (!asked) return;
    let stale = false;
    checkInvitation(fromLink)
      .then((result) => {
        if (!stale) setAnswer({ code: fromLink, state: result.valid ? 'valid' : 'invalid' });
      })
      .catch(() => {
        if (!stale) setAnswer({ code: fromLink, state: 'unchecked' });
      });
    return () => {
      stale = true;
    };
  }, [asked, fromLink]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signUp({ handle, displayName, password, inviteCode });
      navigate(next ?? '/');
    } catch (err) {
      setError(
        err instanceof ApiError
          ? (ca.errors[err.code] ?? ca.errors.unknown)
          : err instanceof OfflineError
            ? ca.errors.offline
            : ca.errors.unknown,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth">
      <div className="wordmark" role="img" aria-label={meta.name}>
        <Wordmark name={meta.name} />
      </div>
      <form className="card form" onSubmit={submit} aria-label={ca.register}>
        <h1>{me.registrationOpen ? ca.registerFirst : ca.register}</h1>
        {me.registrationOpen && <p className="muted small">{ca.firstAccountNote}</p>}
        {needsInvite && linked === 'valid' && (
          <p role="status">
            <span className="chip ok">
              <i className="bi bi-check-circle-fill" aria-hidden="true" /> {ca.inviteValid}
            </span>
          </p>
        )}
        {needsInvite && linked === 'invalid' && (
          <p className="error" role="alert">
            <i className="bi bi-x-circle" aria-hidden="true" /> {ca.inviteInvalid}
          </p>
        )}
        {needsInvite && linked !== 'valid' && (
          <label>
            {ca.inviteCode}
            <input
              name="inviteCode"
              autoComplete="off"
              required
              value={inviteCode}
              onChange={(e) => {
                setInviteCode(e.target.value);
                setTyped(true);
              }}
            />
            <span className="hint">
              {linked === 'checking'
                ? ca.inviteChecking
                : linked === 'unchecked'
                  ? ca.inviteUnchecked
                  : ca.inviteHint}
            </span>
          </label>
        )}
        <label>
          {ca.handle}
          <input
            name="handle"
            autoComplete="username"
            autoCapitalize="none"
            required
            minLength={3}
            maxLength={32}
            pattern="[a-z0-9][a-z0-9._\-]{2,31}"
            value={handle}
            onChange={(e) => setHandle(e.target.value.toLowerCase())}
          />
          <span className="hint">{ca.handleHint}</span>
        </label>
        <label>
          {ca.displayName}
          <input
            name="displayName"
            autoComplete="nickname"
            maxLength={64}
            value={displayName}
            onChange={(e) => setOwnName(e.target.value)}
          />
        </label>
        <label>
          {ca.password}
          <input
            name="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <span className="hint">{ca.passwordHint}</span>
        </label>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <button type="submit" className="primary" disabled={busy}>
          {me.registrationOpen ? ca.registerFirst : ca.register}
        </button>
        <p className="muted small">
          {ca.haveAccount} <Link to={`/signin${nextQuery(next)}`}>{ca.signIn}</Link>
        </p>
      </form>
    </main>
  );
}
